import { useInfiniteQuery } from "@tanstack/react-query";
import clsx from "clsx";
import DataTable from "components/DataTable";
import { WALLET_PENDING_HEADER_LIST } from "constants/haulpay-broker";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { getGenericNextPageAPI, getPendingTransactionAPI } from "services/api/haulpay";
import BrokerWalletPendingItem from "../../../modules/Broker/WalletPendingItem";
import styles from "../History/style.module.scss";
import Button from "components/Button";
import { useSelector } from "react-redux";
import { selectUserDetails } from "store/user/selector";

export default function BrokerWalletPending() {
  const { isDesktop } = useDeviceType();
  const userDetails = useSelector(selectUserDetails);
  const isSelfFinanceUser = userDetails?.allow_self_finance_fr ?? false;
  const isStripeEnabled = userDetails?.stripe_enabled ?? false;
  const itemsPerPage = 3;

  const {
    data,
    error,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage
  } = useInfiniteQuery(
    ["pendingTransactions", isSelfFinanceUser, isStripeEnabled],
    async ({ pageParam = null }) => {
      if (!isSelfFinanceUser && !isStripeEnabled) {
        return { results: [], next: null, previous: null }; // Return empty data structure to avoid undefined errors
      }
      if (pageParam === null) {
        const response = await getPendingTransactionAPI();
        return response.data;
      } else {
        const response = await getGenericNextPageAPI(pageParam);
        return response.data;
      }
    },
    {
      getNextPageParam: (lastPage: any) => lastPage?.next || null,
      getPreviousPageParam: (firstPage: any) => firstPage?.previous || null
    }
  );

  if (isLoading) {
    return <div />;
  }

  if (error || !data) {
    showErrorMessage("Failed to fetch pending transactions.");
    return <p>Error loading transactions.</p>;
  }

  const transactionList = data.pages.flatMap((page: any) => page?.results ?? []) ?? [];
  const totalCount = data.pages[0]?.count ?? 0;

  const nextLink = data.pages[data.pages.length - 1]?.next || null;
  const prevLink = data.pages[0]?.previous || null;
  const fetchMoreData = async (itemsPerPage: number, pageNumberParam?: number) => {
    try {
      if (itemsPerPage > 0 && hasNextPage) {
        await fetchNextPage();
      } else if (itemsPerPage < 0 && hasPreviousPage) {
        await fetchPreviousPage();
      } else if (pageNumberParam !== undefined) {
        // Handle specific page number if needed
      }
    } catch {
      showErrorMessage("Please try again later.");
    }
  };

  return (
    <div className={styles.walletContainer}>
      <div>
        <div className={clsx(isDesktop && "!hidden")}>
          {transactionList.length > 0 ? (
            transactionList.map((item: any) => (
              <BrokerWalletPendingItem key={item?.contract_id} data={item} />
            ))
          ) : (
            <p className="text-center text-placeholder">No Transactions</p>
          )}
        </div>
        <div className={clsx(!isDesktop && "!hidden")}>
          <DataTable
            headers={WALLET_PENDING_HEADER_LIST}
            data={transactionList}
            totalCount={totalCount}
            nextLink={nextLink}
            prevLink={prevLink}
            handlePageChange={fetchMoreData}
          />
        </div>
      </div>
      <div className={styles.paginationButtons}>
        {nextLink && (
          <Button variant="outline" onClick={() => fetchMoreData(itemsPerPage)}>
            {isFetchingNextPage ? "Loading..." : "Next"}
          </Button>
        )}
      </div>
    </div>
  );
}
